import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { queryClient } from '@/App.tsx';
import bookingChangesApi from '@/features/dashboard/bookingChanges/api/bookingChangesApi.ts';
import { bookingChangesApiQueryKeys } from '@/features/dashboard/bookingChanges/api/bookingChangesApi.queryKeys.ts';
import { BookingChangeDetailDto } from '@/features/dashboard/bookingChanges/types/dto/BookingChangeDetailDto.ts';
import { isOpenChangeStatus } from '@/features/dashboard/bookingChanges/types/dto/BookingChangeStatus.utils.ts';

/**
 * Otwarta paczka rezerwacji, albo `null`, gdy żadnej nie ma. Krótki
 * `staleTime`, bo belka szkicu i panel boczny muszą pokazywać stan po każdej
 * edycji pozycji.
 */
const useGetOpenDraft = (bookingId: string | undefined) =>
  useQuery(getOpenDraftQueryOptions(bookingId));

export const getOpenDraftQueryOptions = (
  bookingId: string | undefined,
): UseQueryOptions<BookingChangeDetailDto | null> => ({
  queryKey: bookingChangesApiQueryKeys.openDraft(bookingId ?? ''),
  queryFn: () => bookingChangesApi.getOpenDraft(bookingId!),
  enabled: !!bookingId,
  staleTime: 1000 * 10,
});

/**
 * Czy `changeId` to paczka, którą pamięć podręczna zna jako otwartą.
 *
 * Odpowiedź bierze się prosto z cache'u, a nie z propsów, bo podglądy muszą
 * rozstrzygać także w chwili unieważniania zapytań — zanim ekrany zdążą się
 * przerysować bez zamkniętej właśnie paczki.
 */
export const isCachedOpenChange = (
  bookingId: string | undefined,
  changeId: string | undefined,
): boolean => {
  if (!bookingId || !changeId) {
    return false;
  }
  const draft = queryClient.getQueryData<BookingChangeDetailDto | null>(
    bookingChangesApiQueryKeys.openDraft(bookingId),
  );
  return !!draft && draft.id === changeId && isOpenChangeStatus(draft.status);
};

/**
 * Wpisuje wynik mutacji zamykającej paczkę, żeby podglądy przestały ją uznawać
 * za otwartą jeszcze przed odświeżeniem listy paczek.
 */
export const setCachedOpenDraft = (
  bookingId: string,
  change: BookingChangeDetailDto,
) =>
  queryClient.setQueryData<BookingChangeDetailDto | null>(
    bookingChangesApiQueryKeys.openDraft(bookingId),
    isOpenChangeStatus(change.status) ? change : null,
  );

export default useGetOpenDraft;
